import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap font-[family-name:var(--font-brand)] tracking-[var(--ls-brand)] uppercase font-medium transition-all active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--color-focus-ring) disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-(--btn-primary) text-white shadow-(--shadow-button) hover:bg-(--btn-primary-hover) active:bg-(--btn-primary-active) disabled:opacity-100 disabled:bg-(--bg-overlay-secondary) disabled:text-(--text-tertiary) disabled:shadow-none disabled:border disabled:border-(--border-secondary)",
        destructive:
          "bg-(--btn-error) text-white shadow-sm hover:bg-(--btn-error-hover) active:bg-(--btn-error-active) disabled:opacity-100 disabled:bg-(--bg-overlay-secondary) disabled:text-(--text-tertiary) disabled:shadow-none disabled:border disabled:border-(--border-secondary)",
        outline:
          "border border-(--border-secondary) bg-(--bg-overlay-secondary) text-(--text-accent) hover:bg-(--color-accent-overlay) hover:text-(--text-title-accent)",
        secondary:
          "bg-(--btn-modal-cancel-bg) text-(--btn-modal-cancel-text) border border-(--border-secondary) hover:bg-(--btn-modal-cancel-bg-hover) hover:text-(--btn-modal-cancel-text-hover) active:bg-(--btn-modal-cancel-bg-active)",
        ghost:
          "text-(--text-secondary) hover:bg-(--bg-overlay-secondary) hover:text-(--text-primary)",
        link: "text-(--text-accent) underline-offset-4 hover:underline shadow-none",
      },
      size: {
        default: "btn-compact",
        sm: "btn-compact-sm",
        lg: "h-[var(--height-btn-lg)] px-[var(--space-5)] rounded-[var(--radius-md)] text-size-sm",
        icon: "h-[var(--height-btn)] w-[var(--height-btn)] rounded-[var(--radius-md)]",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  isLoading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      isLoading = false,
      children,
      disabled,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        disabled={disabled || isLoading}
        {...props}
      >
        {isLoading ? (
          <span className="flex items-center justify-center gap-2">
            <svg
              className="animate-spin h-3.5 w-3.5"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            {children}
          </span>
        ) : (
          children
        )}
      </Comp>
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
